import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";
import { Component } from "@theme/component";

/**
 * Resolves Klaviyo public/company ID from the installed app embed script.
 * @returns {string|null}
 */
function getKlaviyoCompanyId() {
  if (window.klaviyoCompanyId) return window.klaviyoCompanyId;

  const scripts = document.querySelectorAll('script[src*="static.klaviyo.com"], script[src*="klaviyo.com"]');
  for (const script of scripts) {
    const src = script.getAttribute("src") || "";
    const match =
      src.match(/\/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js/) ||
      src.match(/company_id=([A-Za-z0-9]+)/) ||
      src.match(/\/([A-Za-z0-9]{6})\//);
    if (match?.[1]) {
      window.klaviyoCompanyId = match[1];
      return match[1];
    }
  }

  return null;
}

/**
 * Build form body for Klaviyo hosted list subscribe (a/g from manage.kmail-lists.com).
 * @param {string} companyId
 * @param {string} listId
 * @param {string} email
 */
function buildKlaviyoListBody(companyId, listId, email) {
  const body = new URLSearchParams();
  body.set("a", companyId);
  body.set("g", listId);
  body.set("email", email);
  body.set("$fields", "$source");
  body.set("$source", "Notify Me");
  return body;
}

/**
 * Subscribe via hidden form POST.
 * Wait for initial about:blank load, THEN submit, THEN wait for result load.
 * @param {string} companyId
 * @param {string} listId
 * @param {string} email
 * @returns {Promise<boolean>}
 */
function subscribeViaHiddenForm(companyId, listId, email) {
  return new Promise((resolve) => {
    const iframeName = `klaviyo_sub_${Date.now()}`;
    const iframe = document.createElement("iframe");
    iframe.name = iframeName;
    iframe.title = "klaviyo-subscribe";
    iframe.hidden = true;
    iframe.style.cssText = "display:none;width:0;height:0;border:0;";
    iframe.src = "about:blank";

    const form = document.createElement("form");
    form.method = "POST";
    form.action = "https://manage.kmail-lists.com/ajax/subscriptions/subscribe";
    form.target = iframeName;
    form.acceptCharset = "UTF-8";
    form.hidden = true;

    for (const [name, value] of buildKlaviyoListBody(companyId, listId, email).entries()) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    const cleanup = (ok) => {
      iframe.removeEventListener("load", onReady);
      iframe.removeEventListener("load", onResult);
      form.remove();
      iframe.remove();
      resolve(ok);
    };

    const onResult = () => cleanup(true);

    const onReady = () => {
      iframe.removeEventListener("load", onReady);
      iframe.addEventListener("load", onResult, { once: true });
      form.submit();
    };

    iframe.addEventListener("load", onReady, { once: true });
    document.body.appendChild(form);
    document.body.appendChild(iframe);
  });
}

/**
 * POST to Klaviyo hosted list endpoint.
 * CORS may hide the response even when the server accepted the subscribe —
 * fall back to mode:'no-cors' so the request is still delivered.
 * @param {string} companyId
 * @param {string} listId
 * @param {string} email
 * @returns {Promise<boolean>}
 */
async function subscribeViaKmailLists(companyId, listId, email) {
  const body = buildKlaviyoListBody(companyId, listId, email);
  const url = "https://manage.kmail-lists.com/ajax/subscriptions/subscribe";

  try {
    const response = await fetch(url, {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
      keepalive: true,
    });
    if (response.ok || response.status === 200 || response.status === 202) return true;
  } catch {
    // CORS can throw even after the server processed a simple form POST
  }

  try {
    await fetch(url, {
      method: "POST",
      mode: "no-cors",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
      keepalive: true,
    });
    return true;
  } catch (error) {
    console.warn("[notify-me] Klaviyo list subscribe failed", error);
    return false;
  }
}

/**
 * Subscribe email to a Klaviyo list (list ID from block settings).
 * Shopify customer form syncs to the default Klaviyo list via the app;
 * list-specific signup uses manage.kmail-lists.com with g=LIST_ID.
 * @param {string} email
 * @param {string} listId
 * @returns {Promise<boolean>}
 */
async function subscribeToKlaviyoList(email, listId) {
  const companyId = getKlaviyoCompanyId();
  if (!listId || !email || !companyId) return false;

  if (await subscribeViaKmailLists(companyId, listId, email)) return true;
  return subscribeViaHiddenForm(companyId, listId, email);
}

class NotifyMe extends HTMLElement {
  /** @type {boolean} */
  #klaviyoPending = false;

  connectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.addEventListener("submit", this.#onFormSubmit);

    // Sync class with Liquid hidden attr (flex utility overrides [hidden] alone)
    this.classList.toggle("hidden", this.hidden);
  }

  disconnectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.removeEventListener("submit", this.#onFormSubmit);
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id);
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const showOnlyOos = this.dataset.showOnlyOutOfStock === "true";
    const resource = event.detail.data.resource;
    const isOutOfStock = resource ? resource.available === false : this.dataset.outOfStock === "true";

    this.dataset.outOfStock = isOutOfStock ? "true" : "false";
    this.#applyVisibility(showOnlyOos, isOutOfStock);

    if (!event.detail.data.html) return;

    const productId = this.dataset.productId;
    let next = null;

    if (productId) {
      const all = event.detail.data.html.querySelectorAll("notify-me");
      for (const el of all) {
        if (el.dataset.productId === productId) {
          next = el;
          break;
        }
      }
    }

    if (!next) {
      next = event.detail.data.html.querySelector("notify-me");
    }

    if (!next) return;

    morph(this, next, { childrenOnly: true });
    this.#applyVisibility(showOnlyOos, isOutOfStock);
  };

  /**
   * @param {boolean} showOnlyOos
   * @param {boolean} isOutOfStock
   */
  #applyVisibility(showOnlyOos, isOutOfStock) {
    const shouldShow = showOnlyOos ? isOutOfStock : true;
    this.hidden = !shouldShow;
    this.classList.toggle("hidden", !shouldShow);
  }

  /**
   * Dual subscribe: Klaviyo list (if configured) then Shopify customer form.
   * @param {SubmitEvent} event
   */
  #onFormSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const notifyForm = form.closest("notify-me-form");
    if (!(notifyForm instanceof HTMLElement)) return;

    const listId = notifyForm.dataset.klaviyoListId;
    if (!listId) return;

    const emailInput = form.querySelector('input[name="contact[email]"]');
    const email = emailInput instanceof HTMLInputElement ? emailInput.value.trim() : "";
    if (!email || !form.checkValidity()) return;

    if (this.#klaviyoPending) return;

    event.preventDefault();
    this.#klaviyoPending = true;

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }

    try {
      await subscribeToKlaviyoList(email, listId);
    } finally {
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
      this.#klaviyoPending = false;
      form.submit();
    }
  };
}

/**
 * Publish waitlist signup to Shopify Customer Events (Web Pixel sandbox).
 * Meta Lead is fired from Customer events via:
 *   analytics.subscribe('waitlist_signup', ...) -> fbq('track', 'Lead', ...)
 * Once per successful waitlist signup (tied to the success dialog).
 */
function trackWaitlistLead() {
  if (window.__zoozWaitlistLeadFired) return;

  const analytics = window.Shopify?.analytics;
  if (!analytics || typeof analytics.publish !== "function") return;

  window.__zoozWaitlistLeadFired = true;
  analytics.publish("waitlist_signup", {
    content_name: "waitlist_pdp",
  });
}

/**
 * Mirrors newsletter-form success/error dialog behaviour after Shopify customer POST.
 */
class NotifyMeForm extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#showMessageDialog();
  }

  #showMessageDialog() {
    const { input, messageDialog } = this.refs;
    const messageDialogRefs = messageDialog?.refs ?? {};
    const { alert, messageErrorSubscribed } = messageDialogRefs;

    const liveUrl = window.location.href;
    const result = liveUrl.includes("form_type=customer");
    const isSubscribed = result && input?.value?.length != 0;
    const isSuccess = alert instanceof HTMLElement && alert.classList.contains("alert--success");

    if (isSubscribed && messageErrorSubscribed && !alert) {
      messageErrorSubscribed.classList.remove("hidden");
    }

    if (isSubscribed || alert) {
      if (!window.isMessageDialogShow) {
        messageDialog && messageDialog.showDialog();
        window.isMessageDialogShow = true;

        // Only on successful signup popup — not click, error, or already-subscribed
        if (isSuccess) {
          trackWaitlistLead();
        }
      }
    }
  }
}

if (!customElements.get("notify-me")) {
  customElements.define("notify-me", NotifyMe);
}

if (!customElements.get("notify-me-form")) {
  customElements.define("notify-me-form", NotifyMeForm);
}
